import React, { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../../utils/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [impersonateName, setImpersonateName] = useState(localStorage.getItem("impersonateName") || null);


  const loadUserFromToken = async () => {
  const token = localStorage.getItem("token");

  if (!token) {
  setIsAuthenticated(false);
  setUser(null);
  setRole(null);
  setSubscription(null);
  setLoading(false);
  return;
}

  let tokenUser;
  try {
    tokenUser = jwtDecode(token)?.user;
  } catch (err) {
    console.error("Invalid token", err);
    logout();
    return;
  }

  setIsAuthenticated(true);
  setUser(tokenUser || null);
  setRole(tokenUser?.role ?? null);

  try {
    // Served from the offline cache when the network is down
    const res = await api.get("/subscriptions/status");
      setSubscription({
        isActive: res.data.hasActiveSubscription,
        plan: res.data.data?.title || "Free",
        endDate: res.data.data?.expires_on || null,
      });

  } catch (err) {
    // Only a rejected token ends the session. Being offline must not log the POS out —
    // that would also strand any orders still waiting to sync.
    if (err.response?.status === 401) {
      logout();
    } else {
      console.warn("Could not verify subscription status (offline?)", err.message);
    }
  } finally {
    setLoading(false);
  }
};

  // 🔄 Run once on app load
  useEffect(() => {
    loadUserFromToken();
  }, []);

  // 🔑 Login
  const login = (token) => {
    localStorage.setItem("token", token);
    setLoading(true);
    loadUserFromToken();
  };

  // 🚪 Logout
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("superAdminToken");
    localStorage.removeItem("impersonateName");
    setIsAuthenticated(false);
    setUser(null);
    setRole(null);
    setSubscription(null);
    setImpersonateName(null);
    setLoading(false);
  };

  // 🎭 Start Impersonating
  const startImpersonating = async (restaurantId) => {
    try {
      const res = await api.post(`/superadmin/impersonate/${restaurantId}`);
      if (res.data.success) {
        localStorage.setItem("superAdminToken", localStorage.getItem("token"));
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("impersonateName", res.data.restaurantName);
        setImpersonateName(res.data.restaurantName);
        
        setLoading(true);
        loadUserFromToken();
        return { success: true };
      }
    } catch (err) {
      console.error("Failed to impersonate", err);
      return { success: false, message: err.response?.data?.message || "Impersonation failed" };
    }
  };

  // 🛑 Stop Impersonating
  const stopImpersonating = () => {
    const superAdminToken = localStorage.getItem("superAdminToken");
    if (superAdminToken) {
      localStorage.setItem("token", superAdminToken);
      localStorage.removeItem("superAdminToken");
      localStorage.removeItem("impersonateName");
      setImpersonateName(null);
      setLoading(true);
      loadUserFromToken();
    } else {
      logout(); // Fallback if lost
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        role,
        isStaff: user?.isStaff ?? false,
        staffRole: user?.staffRole ?? null,
        staffName: user?.staffName ?? null,
        subscription,
        isSubscriptionActive: subscription?.isActive ?? false,
        restaurantId: user?.id ?? null,
        isImpersonating: user?.isImpersonated ?? false,
        impersonateName,
        login,
        logout,
        startImpersonating,
        stopImpersonating,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
